import { useEffect, type ReactNode } from 'react';
import { Link } from 'react-router-dom';
import { Figure, Flow } from '../components/Diagram';

const CURRENCY_CONVERSION_FIGURE = (
  <Figure
    title="From local currency to one report"
    caption="Each subscription bills in its own currency while analytics and finance can read one reporting currency."
  >
    <Flow
      steps={[
        { title: 'Client has a Region', text: 'Billing currency is inherited from it.' },
        { title: 'Subscription bills locally', text: 'Invoices are in the native currency, such as EUR.', tone: 'accent' },
        { title: 'Lifecycle Event', text: 'Stores the native and the converted amount.' },
        { fork: [
          { title: 'Reporting currency', text: 'Default view: converted and summed.', tone: 'accent' },
          { title: 'By currency', text: 'Open invoices shown per currency, unconverted.', tone: 'muted' },
        ] },
      ]}
    />
  </Figure>
);

const STEPS: { title: string; body: string; figure?: ReactNode; entity?: string; entityLabel?: string }[] = [
  {
    title: 'Choose your reporting currency',
    body: 'Go to Settings → General Settings and set the currency all dashboards and analytics roll up to — typically USD or EUR. This is a tenant-wide setting and is the single currency in which MRR, ARR, NRR, and churn figures are expressed.',
    entity: 'tenant',
    entityLabel: 'Tenant',
  },
  {
    title: 'Create a Region per market',
    body: 'Go to Catalog → Regions and add one Region for each currency market you sell into. Give each a short code (e.g. "EU"), a display name ("Europe"), and a default currency (EUR). You can also list additional accepted currencies on the same region — for example, a "Europe" region may accept EUR and CHF.',
    entity: 'region',
    entityLabel: 'Region',
  },
  {
    title: 'Publish Variable Component Prices per currency',
    body: 'For each Variable Component in your catalog, publish a Global default price row listing every currency you sell in — USD: $5, EUR: €4.50, GBP: £4. Optionally override at the Bundle or Module scope when a specific plan should price the component differently. When a subscription is created in EUR, Revvly walks the scopes and picks the first row containing EUR; if none of the more-specific scopes carry EUR, the Global default applies.',
    entity: 'variableComponentPrice',
    entityLabel: 'Variable Component Price',
  },
  {
    title: 'Assign each Client to their Region',
    body: 'When creating or editing a Client, set their Region. Their default billing currency is inherited from that region. If a customer in your EU region signs up, their subscription and invoices default to EUR — no per-subscription currency selection needed.',
    entity: 'client',
    entityLabel: 'Client',
  },
  {
    title: 'Exchange rates sync automatically',
    body: 'Revvly fetches daily rates from Fixer and stores them once for the whole platform, shared by every workspace. When a subscription is created, the rate active on that date is recorded on the subscription record for historical accuracy. You never need to manage rates manually, and there is no screen for editing them.',
    entity: 'exchangeRate',
    entityLabel: 'Exchange Rate',
  },
  {
    title: 'Analytics roll up to your reporting currency',
    body: 'MRR, ARR, NRR, and all dashboard figures appear in your reporting currency regardless of what individual subscriptions bill in. Lifecycle Events store both the native currency amount and the converted reporting-currency amount, so historical analytics remain accurate even if FX rates shift later.',
    entity: 'lifecycleEvent',
    entityLabel: 'Lifecycle Event',
    figure: CURRENCY_CONVERSION_FIGURE,
  },
  {
    title: 'Finance shows both views',
    body: 'The Invoices & Billing page defaults to "Reporting Currency" — receivables, pending, overdue, and this-week amounts are all FX-converted and summed into a single figure. Use the "By currency" toggle to switch to a per-currency breakdown, where each currency\'s open invoices are displayed separately without conversion.',
    entity: 'invoice',
    entityLabel: 'Invoice',
  },
];

export function MultiCurrencyGuide() {
  useEffect(() => {
    window.scrollTo(0, 0);
    document.title = 'Set up multi-currency billing — Revvly Docs';
  }, []);

  return (
    <div className="tn-page">
      <nav className="dp-crumbs" aria-label="Breadcrumb">
        <Link to="/docs/guides">Guides</Link>
        <span aria-hidden="true">›</span>
        <span className="dp-crumb-current">Multi-currency billing</span>
      </nav>

      <header className="dp-header">
        <span className="rv-eyebrow-mini">Guide</span>
        <h1>
          Multi-currency <em>billing</em>.
        </h1>
        <p className="dp-oneliner">
          Sell in local currencies while keeping a single reporting currency. Seven steps from setup
          to analytics — exchange rates are handled automatically.
        </p>
      </header>

      {STEPS.map((step, i) => (
        <div key={i} className="dp-section">
          <h2>
            <span style={{ color: 'var(--rv-fg-dark-5)', fontWeight: 400, marginRight: 12 }}>
              {String(i + 1).padStart(2, '0')}
            </span>
            {step.title}
          </h2>
          <p className="dp-plain">{step.body}</p>
          {step.figure}
          {step.entity && (
            <ul className="dp-connects" style={{ marginTop: 14 }}>
              <li>
                <Link to={`/docs/concepts/${step.entity}`} style={{ color: 'inherit', textDecoration: 'none' }}>
                  {step.entityLabel} concept ›
                </Link>
              </li>
            </ul>
          )}
        </div>
      ))}
    </div>
  );
}
